import React from 'react';
import { useLazyFetchClientLoginEventsQuery } from "../../../services/clientauth";

export type ClientDashboardLoginEventDto = {
    id: string
    masterAccountLoginId: string
    masterAccountId: string
    event: number
    eventDescription: string
    message: string | null
    loggedByIpAddress: string
    loggedOnDateUtc: string
};

export type ClientDashboardLoginEventsContextProps = {
    clear: () => void,
    data: ClientDashboardLoginEventDto[],
    error: Error | string | null,
    hasMore: boolean,
    isFetching: boolean,
    isLoading: boolean,
    isNextPageLoading: boolean,
    isNewRecords: boolean,
    isRowLoaded: boolean,
    limit: number,
    loadMore: () => void,
    newRecordsSinceCache: number,
    page: number,
    pagination: Pagination,
    setPageLimit: (v: number) => void,
    setSearchTerm: (v: string) => void,
    sort: string,
    sortTable: (property: string) => void,
    totalCount: number,
    totalPages: number
};

export type ClientDashboardLoginEventsProviderProps = {
    children: JSX.Element | JSX.Element[]
    filter?: any | null
    isReady?: boolean
    masterAccountId: string
    masterAccountLoginId: string
    query?: any
};

const ClientDashboardLoginEventsContext = React.createContext<ClientDashboardLoginEventsContextProps | null>(null);

export const useClientDashboardLoginEvents = () => React.useContext(ClientDashboardLoginEventsContext);

const ClientDashboardLoginEventsProvider = ({
    children,
    filter = null,
    isReady = true,
    masterAccountId,
    masterAccountLoginId,
    query = useLazyFetchClientLoginEventsQuery
}: ClientDashboardLoginEventsProviderProps) => {
    const [sort, setSort] = React.useState("LoggedOnDateUtc+desc");
    const [pageLimit, setPageLimit] = React.useState(50);
    const [searchTerm, setSearchTerm] = React.useState("");
    const [isClearing, setIsClearing] = React.useState(false);
    const [cacheKey, setCacheKey] = React.useState(new Date().valueOf());
    const [isLoadingState, setIsLoadingState] = React.useState(false);
    const [load, { data, isLoading: isLoadingData, isFetching, ...loadResults }] = query();

    const { pagination, results, newRecordsSinceCache } = data || { pagination: { page: 1, pageSize: pageLimit, totalCount: 0, totalPages: 1 }, results: [], newRecordsSinceCache: 0 };
    const { page, totalCount, totalPages } = pagination;
    const hasMore = totalCount > 0 && page < totalPages;
    const isLoading = isLoadingData === true;
    const isNextPageLoading = isFetching === true || isLoadingData === true || isLoadingState === true;

    const buildOptions = (cacheKey, page) => {
        return filter != null
            ? {
                ...filter,
                cacheKey: cacheKey,
                masterAccountId,
                masterAccountLoginId,
                sort,
                page,
                limit: pageLimit
            }
            : {
                cacheKey: cacheKey,
                masterAccountId,
                masterAccountLoginId,
                searchTerm,
                sort,
                page,
                limit: pageLimit
            }
    };

    const clear = () => {
        if ((!masterAccountId && !filter) || isNextPageLoading || !isReady) {
            return;
        }

        const newCacheKey = new Date().valueOf();
        const queryOptions = buildOptions(newCacheKey, 1);

        setIsLoadingState(_ => true);
        setIsClearing(_ => true);
        load(queryOptions).unwrap().then(_ => {
            setCacheKey(_prev => newCacheKey);
        }).finally(_ => {
            setIsLoadingState(_prev => false);
            setIsClearing(_ => false);
        });
    };

    const isRowLoaded = (index) => {
        return !hasMore || index < results.length;
    };

    const loadMore = () => {
        if (isNextPageLoading || !hasMore || !isReady) {
            return;
        }

        setIsLoadingState(_ => true);
        load(buildOptions(cacheKey, (page + 1))).unwrap().then(_ => {
            setIsLoadingState(_prev => false);
        });
    };

    const sortTable = (property) => {
        // we want the sort to initially be descending ??
        let newDirection : string | null = "";
        let [sortProperty, direction] = sort.split("+");

        // if the current property is already selected, reverse the sort direction
        if (property === sortProperty) {
            newDirection = direction === "DESC" ? null : "+DESC";
        }

        setSort(property + (newDirection ?? ""));
        clear();
    };

    React.useEffect(clear, [ searchTerm, sort, masterAccountId, masterAccountLoginId, filter, isReady ]);

    return (
        <ClientDashboardLoginEventsContext.Provider value={{
            ...loadResults,
            clear,
            data: isClearing ? [] : results,
            hasMore,
            isFetching,
            isLoading,
            isNextPageLoading,
            isNewRecords: newRecordsSinceCache > 0,
            isRowLoaded,
            limit: pageLimit,
            loadMore: isNextPageLoading ? () => {} : loadMore,
            newRecordsSinceCache,
            page,
            pagination,
            searchTerm,
            setSearchTerm,
            sort,
            sortTable,
            totalCount,
            totalPages
        }}>
            {children}
        </ClientDashboardLoginEventsContext.Provider>
    );
};

export default ClientDashboardLoginEventsProvider;